import { FC, useEffect, useMemo, useState } from 'react'

import { GeoBlockingTemplate } from '@/components/_legacy/GeoBlocking/types'
import { GeoBlockingTemplates } from '@/components/GeoBlockingTemplatesList/types'
import { streamingRestApi } from '@/service/streamingApi'

import { SelectBox } from '../SelectBox'
import { SELECT_BOX_NONE_OPTION_VALUE } from '../SelectBox/SelectBox.constants'

type Props = {
  onChange: (template: GeoBlockingTemplate | null) => void
  disabled?: boolean
  error?: string
  label?: string
}

type TemplateOption = {
  id: number
  name: string
  isWhiteList: boolean
  geoListField: string[]
  template: GeoBlockingTemplate
}

export const GeoBlockingTemplateSelect: FC<Props> = ({
  onChange,
  disabled,
  error,
  label = 'Geo Blocking Template'
}) => {
  const [templates, setTemplates] = useState<GeoBlockingTemplates>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const loadTemplates = async () => {
      setIsLoading(true)

      try {
        const data =
          await streamingRestApi.endpoints.geoBlockingTemplatesEndpoints.getGeoBlockingTemplate()

        const transformedTemplates = data.map((template: { geoListField: string }) => ({
          ...template,
          geoListField: JSON.parse(template.geoListField)
        }))

        setTemplates(transformedTemplates)
      } catch (err) {
        console.error('Error loading geo blocking templates:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadTemplates()
  }, [])

  const templateOptions = useMemo((): TemplateOption[] => {
    return templates.map((template) => ({
      id: template.id,
      name: template.name,
      isWhiteList: template.isWhiteList,
      geoListField: template.geoListField,
      template
    }))
  }, [templates])

  const handleChange = (option: TemplateOption | null | typeof SELECT_BOX_NONE_OPTION_VALUE) => {
    if (option === SELECT_BOX_NONE_OPTION_VALUE || option === null) {
      onChange(null)
    } else {
      onChange(option.template)
    }
  }

  return (
    <SelectBox
      options={templateOptions}
      label={label}
      loading={isLoading}
      disabled={disabled}
      error={error}
      clearable
      noneOption="No Template"
      getOptionId={(option) => option.id.toString()}
      formatInputOption={(option) => option.name}
      formatListOption={(option) => {
        const territoryCount = option.geoListField.length
        const listType = option.isWhiteList ? 'whitelisted' : 'blacklisted'
        return `${option.name} (${territoryCount} territories ${listType})`
      }}
      onChange={handleChange}
      noOptionsMessage="No geo blocking templates available"
    />
  )
}
